import './App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import GlobalLoadingBar from './components/GlobalLoadingBar';
import GlobalFeedback from './components/GlobalFeedback';
import Loader from './components/Loader';

// Route-level code splitting. The consumer scan landing (PublicProductPage) is
// by far the most-hit "first visit", so the heavy admin panel (`./pages` — MUI
// DataGrid, PDF tooling, socket.io, the whole dashboard) is pulled only when
// its route is actually entered, not on every page load.
const Page = lazy(() => import('./pages'));
const PublicProductPage = lazy(() => import('./pages/PublicProductPage'));

const RouteFallback = () => <Loader label="Loading…" minHeight="100vh" />;

function PublicProductRoute() {
  const [searchParams] = useSearchParams();
  const qrcodeKey = searchParams.get('qrcode');

  if (qrcodeKey) {
    // Backward compatibility: qrcode can be a full URL, extract /product/:id/:qrcodeId if present.
    const match = String(qrcodeKey).match(/\/product\/([^/?#]+)\/([^/?#]+)/i);
    if (match) {
      return <Navigate to={`/product/${encodeURIComponent(match[1])}/${encodeURIComponent(match[2])}`} replace />;
    }
    return <PublicProductPage qrcodeKey={qrcodeKey} />;
  }

  // If no qrcode parameter, show admin page
  return <Page />;
}

function PublicProductPageWrapper() {
  const { productId, qrcodeId } = useParams();
  return <PublicProductPage productId={productId} qrcodeId={qrcodeId} />;
}

// GS1 Digital Link: /01/<GTIN>/21/<item number>
function Gs1ProductPageWrapper() {
  const { gtin, serial } = useParams();
  return <PublicProductPage gtin={gtin} serial={serial} />;
}

function App() {
  return (
    <div className="App">
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalLoadingBar />
          <GlobalFeedback />
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/product/:productId/:qrcodeId" element={<PublicProductPageWrapper />} />
                <Route path="/01/:gtin/21/:serial" element={<Gs1ProductPageWrapper />} />
                <Route path="/" element={<PublicProductRoute />} />
                <Route path="/admin/*" element={<Page />} />
                {/* The separate Staff Login is gone — staff sign in on the
                    normal login (their email is detected as an employee). Old
                    /staff links land there. */}
                <Route path="/staff/*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  );
}

export default App;
